import { DataTableColDef, GRID_CHECKBOX_SELECTION_COL_DEF } from '@chronosphereio/chrono-ui';
import { useMemo } from 'react';
import { LegendItem, TableLegendPinnedColumns } from '../legend-model';

export const PINNED_COLUMNS_JUST_CHECKBOX: TableLegendPinnedColumns = {
  // We always pin the checkbox to the left. This is a relatively narrow column and is very useful to keep in view
  // for toggling things on/off.
  left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
};

export const PINNED_COLUMNS_NO_PINNING: TableLegendPinnedColumns = {
  left: [],
  right: [],
};

// Determines the min width that should be shown to non-pinned columns. When this space cannot be provided, responsive
// handling will fall back to the default pinned columns config to ensure a viable user experience.
export const MIN_NON_PINNED_COLUMNS_WIDTH = 250;

interface UsePinnedTableLegendColumnsProps {
  width: number;
  pinnedColumns?: TableLegendPinnedColumns;
  columns: Array<DataTableColDef<LegendItem>>;
  hasScrollX?: boolean;
}
interface UsePinnedTableLegendColumnsResponse {
  pinnedColumns?: TableLegendPinnedColumns;
}

/**
 * Hook that helps manage pinned columns.
 *
 * When there is not a horizontal scrollbar, it returns a config with no pinned columns because pinning is not
 * useful when you can see all columns without scrolling.
 *
 * When there is a horizontal scrollbar, it does the following:
 * - Merges any custom pinning configuration with the default pinning config.
 * - Evaluates if pinning will leave enough space to view non-pinned columns, and does one of the
 *   following:
 *   - If there is not enough space, it returns the default pinning config, which is safe for all widths.
 *   - IF there is enough space, it returns the merged pinning config.
 *
 * Note that the "responsive" logic in this hook currently only accounts for columns with explicit, number-based widths.
 * It cannot currently account for dynamic columns.
 */
export const usePinnedTableLegendColumns = ({
  columns,
  width,
  pinnedColumns: initPinnedColumns,
  hasScrollX,
}: UsePinnedTableLegendColumnsProps): UsePinnedTableLegendColumnsResponse => {
  // We only show pinned columns when there is a horizontal scrollbar. When everything is visible without
  // scrolling, pinning is not necessary and the pinned columns just add visual noise.
  const enablePinning = !!hasScrollX;

  const mergedPinnedColumns: TableLegendPinnedColumns | undefined = useMemo(() => {
    if (!initPinnedColumns || !enablePinning) {
      // Short-circuit if no merging is needed.
      return;
    }

    return {
      left: (PINNED_COLUMNS_JUST_CHECKBOX.left ?? []).concat(initPinnedColumns.left ?? []),
      right: (PINNED_COLUMNS_JUST_CHECKBOX.right ?? []).concat(initPinnedColumns.right ?? []),
    };
  }, [enablePinning, initPinnedColumns]);

  const pinnedColumnsWidth: number | undefined = useMemo(() => {
    if (!mergedPinnedColumns || !enablePinning) {
      // Short-circuit if we do not have custom pinned columns. The "default" config is just the checkbox and does not
      // need the responsive math. We should only pay the cost for the math when it's needed.
      return;
    }

    // Attempt to calculate the width of the pinned columns, so we can handle the feature responsively.
    const pinnedColumnIds = new Set([...(mergedPinnedColumns.left ?? []), ...(mergedPinnedColumns.right ?? [])]);
    return columns.reduce((totalWidth, columnConfig) => {
      // Column is pinned, check if it has a width we can add to the total.
      if (pinnedColumnIds.has(columnConfig.field)) {
        if (columnConfig.width && typeof columnConfig.width === 'number') {
          return totalWidth + columnConfig.width;
        }
      }

      return totalWidth;
    }, 0);
  }, [columns, enablePinning, mergedPinnedColumns]);

  // Intentionally splitting out the `useMemo`s in this hook to separate the more expensive operations (e.g.
  // merging arrays, calculating column widths) from the code that runs every time the legend width changes. This
  // hopefully keeps this responsive handling relatively performant.
  const pinnedColumns = useMemo(() => {
    if (!enablePinning) {
      // Returning an empty pinning config (instead of `undefined`) when we disable pinning because the
      // datagrid does not always update properly when you pass `undefined`.
      return PINNED_COLUMNS_NO_PINNING;
    }

    if (!mergedPinnedColumns || typeof pinnedColumnsWidth !== 'number') {
      // If we do not have any information about merged columns or pinned columns widths, we do not need this responsive
      // check. Return the default pinned config.
      return PINNED_COLUMNS_JUST_CHECKBOX;
    }

    // If we do have custom config, do some math to determine if it will cause responsive issues.
    const nonPinnedColumnWidth = width - pinnedColumnsWidth;

    // When the pinned columns will lead to a bad user experience because they do not leave enough
    // space for non-pinned columns to be viewed by the user, we fall back to the default (only
    // pinning the checkbox column).
    if (nonPinnedColumnWidth < MIN_NON_PINNED_COLUMNS_WIDTH) {
      return PINNED_COLUMNS_JUST_CHECKBOX;
    }

    // No responsiveness issues, return the custom pinning configuration.
    return mergedPinnedColumns;
  }, [enablePinning, mergedPinnedColumns, pinnedColumnsWidth, width]);

  return {
    pinnedColumns,
  };
};
